import React from "react";
import { Logo } from "@/components/ui/logo";

interface InitialLoadingScreenProps {
  message?: string;
}

export function InitialLoadingScreen({ message = "Initializing Aegis..." }: InitialLoadingScreenProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-400/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-400/20 rounded-full blur-3xl animate-pulse [animation-delay:1s]"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-full blur-3xl animate-spin [animation-duration:20s]"></div>
      </div>

      <div className="relative z-10 flex flex-col items-center gap-8 p-12 rounded-3xl bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl border border-white/20 dark:border-slate-700/20 shadow-2xl">
        {/* Main logo with enhanced animation */}
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full blur-xl opacity-30 animate-pulse"></div>
          <Logo size="xl" showText={true} className="relative animate-bounce" />
        </div>

        {/* Loading message */}
        <div className="text-center space-y-4">
          <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            {message}
          </h2>
          <p className="text-muted-foreground max-w-sm">
            Setting up your secure digital continuity workspace...
          </p>
        </div>

        {/* Enhanced loading animation */}
        <div className="flex items-center space-x-2">
          <div className="flex space-x-1">
            <div className="w-3 h-3 bg-blue-500 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
            <div className="w-3 h-3 bg-blue-600 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
            <div className="w-3 h-3 bg-blue-700 rounded-full animate-bounce"></div>
          </div>
          <div className="w-px h-6 bg-border mx-4"></div>
          <div className="flex space-x-1">
            <div className="w-3 h-3 bg-purple-500 rounded-full animate-bounce [animation-delay:0.1s]"></div>
            <div className="w-3 h-3 bg-purple-600 rounded-full animate-bounce [animation-delay:0.2s]"></div>
            <div className="w-3 h-3 bg-purple-700 rounded-full animate-bounce [animation-delay:0.3s]"></div>
          </div>
        </div>

        {/* Progress indicator */}
        <div className="w-64 space-y-2">
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-blue-500 via-purple-500 to-blue-500 rounded-full animate-pulse"
                 style={{
                   backgroundSize: '200% 100%',
                   animation: 'shimmer 2s infinite'
                 }}>
            </div>
          </div>
          <p className="text-xs text-center text-muted-foreground">
            This may take a few moments on first load
          </p>
        </div>
      </div>

      <style jsx>{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>
    </div>
  );
}

export default InitialLoadingScreen;